import * as React from 'react';
import { TableVirtuoso } from 'react-virtuoso';

import PropTypes from 'prop-types';

import { TableSortLabel } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { styled } from '@mui/material/styles';

import { getIsColumnSorted, getNewSortKey, getSortDirection } from './utils';

const ignoredProps = ['isClickable', 'rowIndex'];

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => !ignoredProps.includes(prop),
})(({ theme, isClickable, rowIndex }) => ({
  ...(isClickable && {
    cursor: 'pointer',
  }),
  '& th, & td': {
    borderBottom: 'none',
  },
  ...(!(rowIndex % 2) && {
    backgroundColor: theme.palette.action.hover,
  }),
  '&:hover': {
    backgroundColor: '#ecedee',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const getTableComponents = (onRowClick) => ({
  Scroller: React.forwardRef((props, ref) => (
    <TableContainer {...props} ref={ref} />
  )),
  Table: (props) => (
    <Table
      {...props}
      sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }}
    />
  ),
  TableHead,
  // eslint-disable-next-line react/prop-types
  TableRow: ({ item, ...props }) => {
    // eslint-disable-next-line react/prop-types
    const rowIndex = props['data-index'];
    return (
      <StyledTableRow
        {...props}
        onClick={() => onRowClick({ original: item })}
        isClickable={Boolean(onRowClick)}
        rowIndex={rowIndex}
      />
    );
  },
  TableBody: React.forwardRef((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
});

const fixedHeaderContent = (columns, sortKey, onSortChange) => () => (
  <StyledTableRow>
    {columns.map(({ id, numeric = false, minWidth, Header }) => (
      <TableCell
        key={id}
        variant="head"
        align={numeric ? 'right' : 'left'}
        style={{ minWidth }}
        sortDirection={
          getIsColumnSorted(sortKey, id) ? getSortDirection(sortKey, id) : false
        }
        sx={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          backgroundColor: 'background.paper',
        }}
      >
        <TableSortLabel
          active={getIsColumnSorted(sortKey, id)}
          direction={getSortDirection(sortKey, id)}
          onClick={() => onSortChange(getNewSortKey(sortKey, id))}
        >
          {Header}
        </TableSortLabel>
      </TableCell>
    ))}
  </StyledTableRow>
);

const rowContent = (columns) => (_index, row) => (
  <>
    {columns.map(({ id, numeric = false, accessor }) => (
      <TableCell
        key={id}
        align={numeric ? 'right' : 'left'}
        sx={{ fontSize: '1.6rem' }}
      >
        {accessor(row)}
      </TableCell>
    ))}
  </>
);

const VirtualizedTable = (props) => {
  const { columns, entries, onRowClick, sortKey, onSortChange } = props;

  const components = getTableComponents(onRowClick);
  const headerContent = fixedHeaderContent(columns, sortKey, onSortChange);
  const itemContent = rowContent(columns);

  return (
    <Paper sx={{ my: 1 }} elevation={0} square>
      <TableVirtuoso
        data={entries}
        components={components}
        fixedHeaderContent={headerContent}
        itemContent={itemContent}
        useWindowScroll
      />
    </Paper>
  );
};

VirtualizedTable.propTypes = {
  columns: PropTypes.instanceOf(Array).isRequired,
  entries: PropTypes.instanceOf(Array).isRequired,
  onRowClick: PropTypes.func,
  onSortChange: PropTypes.func,
  sortKey: PropTypes.string,
};

export default VirtualizedTable;
